/**
 * Suggested starter glossary for a Swedish AV company working with Chinese manufacturers.
 *
 * Swedish column: the FIRST term is what Swedish translations will use. Swedish AV/IT people
 * mostly use the English technical words (switch, scaler, routing …), so those come first; the
 * Swedish words are kept as synonyms so they are still recognised when someone writes them.
 * Chinese terms are common industry usage but MUST be reviewed by a Chinese-speaking colleague.
 */
export const STARTER_MARK = 'Förslag';
const REVIEW = `${STARTER_MARK} – granska kinesiskan`;

type Row = { sv?: string[]; zh?: string[]; en?: string[]; note?: string; keep?: boolean };

const NAMES = [
  'Dante', 'AES67', 'NDI', 'EDID', 'HDCP', 'HDMI', 'HDBaseT', 'DisplayPort', 'SDI', 'CEC', 'IGMP', 'VLAN', 'PoE', 'DSP', 'AEC',
  'KVM', 'PTZ', 'RS-232', 'API', 'USB-C', 'RMA',
];

const ROWS: Row[] = [
  ...NAMES.map((n) => ({ en: [n], keep: true, note: `${STARTER_MARK} – namn/förkortning, översätts inte` })),

  // AV / IT technology — English first in Swedish, as spoken in the trade
  { sv: ['distribution', 'distribuera'], zh: ['分发'], en: ['distribution'], note: `En källa till många mottagare. ${REVIEW}` },
  { sv: ['switch', 'nätverksswitch'], zh: ['交换机'], en: ['network switch'], note: `Nätverksswitch (inte videoväxlare). ${REVIEW}` },
  { sv: ['switcher', 'videoswitcher', 'växlare'], zh: ['切换器'], en: ['switcher', 'video switcher'], note: `Videoväxlare. ${REVIEW}` },
  { sv: ['matrix', 'matrisväxlare', 'matris'], zh: ['矩阵切换器'], en: ['matrix switcher', 'matrix'], note: REVIEW },
  { sv: ['routing', 'routning', 'koppling'], zh: ['路由'], en: ['routing'], note: `Vilken källa som går till vilken mottagare. ${REVIEW}` },
  { sv: ['scaler', 'skalare'], zh: ['缩放器'], en: ['scaler'], note: REVIEW },
  { sv: ['extender'], zh: ['延长器'], en: ['extender'], note: REVIEW },
  { sv: ['splitter'], zh: ['分配器'], en: ['splitter'], note: REVIEW },
  { sv: ['encoder', 'kodare'], zh: ['编码器'], en: ['encoder'], note: REVIEW },
  { sv: ['decoder', 'avkodare'], zh: ['解码器'], en: ['decoder'], note: REVIEW },
  { sv: ['codec'], zh: ['编解码器'], en: ['codec'], note: REVIEW },
  { sv: ['sändare', 'transmitter'], zh: ['发送器'], en: ['transmitter'], note: REVIEW },
  { sv: ['mottagare', 'receiver'], zh: ['接收器'], en: ['receiver'], note: REVIEW },
  { sv: ['multicast'], zh: ['组播'], en: ['multicast'], note: REVIEW },
  { sv: ['unicast'], zh: ['单播'], en: ['unicast'], note: REVIEW },
  { sv: ['bitrate', 'bithastighet'], zh: ['码率'], en: ['bitrate', 'bit rate'], note: REVIEW },
  { sv: ['bandbredd', 'bandwidth'], zh: ['带宽'], en: ['bandwidth'], note: REVIEW },
  { sv: ['bildfrekvens', 'framerate', 'frame rate'], zh: ['帧率'], en: ['frame rate', 'framerate'], note: REVIEW },
  { sv: ['upplösning'], zh: ['分辨率'], en: ['resolution'], note: REVIEW },
  { sv: ['latens', 'latency', 'fördröjning'], zh: ['延迟'], en: ['latency'], note: REVIEW },
  { sv: ['firmware', 'fast programvara'], zh: ['固件'], en: ['firmware'], note: REVIEW },
  { sv: ['preset'], zh: ['预设'], en: ['preset'], note: REVIEW },
  { sv: ['gain', 'förstärkning'], zh: ['增益'], en: ['gain'], note: REVIEW },
  { sv: ['mute'], zh: ['静音'], en: ['mute'], note: REVIEW },
  { sv: ['line level', 'linjenivå'], zh: ['线路电平'], en: ['line level'], note: REVIEW },
  { sv: ['phantom power', 'fantommatning'], zh: ['幻象电源'], en: ['phantom power'], note: REVIEW },
  { sv: ['beamforming'], zh: ['波束成形'], en: ['beamforming'], note: REVIEW },
  { sv: ['ekosläckning', 'echo cancellation', 'ekoeliminering'], zh: ['回声消除'], en: ['echo cancellation'], note: REVIEW },
  { sv: ['förstärkare'], zh: ['功放'], en: ['amplifier'], note: REVIEW },
  { sv: ['högtalare'], zh: ['扬声器'], en: ['loudspeaker', 'speaker'], note: REVIEW },
  { sv: ['mikrofon'], zh: ['麦克风'], en: ['microphone'], note: REVIEW },
  { sv: ['källa', 'signalkälla'], zh: ['信号源'], en: ['source'], note: REVIEW },
  { sv: ['ingång', 'input'], zh: ['输入'], en: ['input'], note: REVIEW },
  { sv: ['utgång', 'output'], zh: ['输出'], en: ['output'], note: REVIEW },
  { sv: ['styrsystem', 'control system'], zh: ['控制系统'], en: ['control system'], note: REVIEW },
  { sv: ['styrprotokoll'], zh: ['控制协议'], en: ['control protocol'], note: REVIEW },
  { sv: ['rack'], zh: ['机柜'], en: ['rack'], note: REVIEW },
  { sv: ['kabelstege'], zh: ['电缆桥架'], en: ['cable tray'], note: REVIEW },
  { sv: ['kretskort', 'PCB'], zh: ['电路板'], en: ['circuit board', 'PCB'], note: REVIEW },
  { sv: ['ritning'], zh: ['图纸'], en: ['drawing'], note: REVIEW },
  { sv: ['datablad', 'datasheet'], zh: ['规格书'], en: ['datasheet'], note: REVIEW },

  // Purchasing & production
  { sv: ['artikelnummer', 'part number'], zh: ['料号'], en: ['part number'], note: REVIEW },
  { sv: ['inköpsorder', 'PO', 'beställning'], zh: ['采购订单'], en: ['purchase order', 'PO'], note: REVIEW },
  { sv: ['offert'], zh: ['报价'], en: ['quotation', 'quote'], note: REVIEW },
  { sv: ['leveransdatum'], zh: ['交货日期'], en: ['delivery date'], note: REVIEW },
  { sv: ['ledtid', 'lead time'], zh: ['交期'], en: ['lead time'], note: REVIEW },
  { sv: ['MOQ', 'minsta orderkvantitet'], zh: ['最小起订量'], en: ['MOQ', 'minimum order quantity'], note: REVIEW },
  { sv: ['first article', 'förstaartikelkontroll', 'FAI'], zh: ['首件检验'], en: ['first article inspection', 'FAI'], note: REVIEW },
  { sv: ['prov', 'provexemplar', 'sample'], zh: ['样品'], en: ['sample'], note: REVIEW },
  { sv: ['kartongmärkning'], zh: ['外箱标签'], en: ['carton labelling'], note: REVIEW },
  { sv: ['garanti'], zh: ['保修'], en: ['warranty'], note: REVIEW },
  { sv: ['certifiering'], zh: ['认证'], en: ['certification'], note: REVIEW },
];

export const GLOSSARY_STARTER = ROWS.map((r) => ({
  terms: {
    ...(r.sv ? { 'sv-SE': r.sv } : {}),
    ...(r.zh ? { 'zh-Hans': r.zh } : {}),
    ...(r.en ? { en: r.en } : {}),
  },
  keepOriginal: !!r.keep,
  note: r.note ?? null,
}));
